<template>
  <v-card>
    <v-card-title>
      <h2 class="card-title">{{ $t('base.edit_group') }}</h2>
    </v-card-title>
    <v-card-content>
      <v-input
        class="mb-3"
        :label="$t('base.name')"
        :focus="true"
        :hint="errorMessage('name')"
        :invalid="!!errorMessage('name')"
        v-model="form.name"
      />
      <v-input
        class="mb-3"
        :label="$t('base.animal_count')"
        :hint="errorMessage('animal_count')"
        :invalid="!!errorMessage('animal_count')"
        v-model="form.animal_count"
      />
      <v-type-select
        v-model="form.typeName"
        :label="$t('base.animal_type')"
        :hint="errorMessage('type')"
        :invalid="!!errorMessage('type')"
        :custom-types="filteredCustomTypes"
        :default-types="filteredDefaultTypes"
        @select="onSelectType"
        @create-enabled="onCreateEnabled"
      />
    </v-card-content>
    <v-card-actions>
      <v-btn
        class="mr-2"
        :disabled="!canSave"
        :loading="loading"
        @click="onUpdate"
      >
        {{ $t('base.save') }}
      </v-btn>
      <delete-btn
        :disabled="!hasPermission('group', 'delete')"
        @delete="onDelete"
      />
    </v-card-actions>
  </v-card>
</template>

<script>
import api from '@/api'
import { mapActions, mapGetters } from 'vuex'
import formErrorsMixin from '@/mixins/form-errors.mixin.js'

import DeleteBtn from '@/components/common/delete-btn/DeleteBtn'

export default {
  name: 'SettingsGroupModal',

  mixins: [formErrorsMixin],

  props: {
    data: {
      type: Object,
      default: null
    }
  },

  components: {
    DeleteBtn
  },

  data: () => ({
    form: {},
    errors: {
      name: null,
      type: null
    },
    loading: false,
    selectedType: null,
    isDefaultType: false,
    createTypeEnabled: false,
    creatingNewType: false,
    originalTypeName: ''
  }),

  mounted () {
    this.form = { ...this.data }

    if (this.form.animal_type && this.form.animal_type.name) {
      this.form.typeName = this.form.animal_type.name
      this.originalTypeName = this.form.animal_type.name

      this.selectedType = this.form.animal_type
      this.createTypeEnabled = true
    }

    this.loadData()
  },

  computed: {
    ...mapGetters('animaltypes', [
      'animalTypes',
      'defaultAnimalTypes'
    ]),

    filteredCustomTypes () {
      return this.animalTypes.filter(type => !type.deleted)
    },

    filteredDefaultTypes () {
      const customNames = this.animalTypes
        .filter(type => !type.deleted)
        .map(t => t.name.toLowerCase())

      return this.defaultAnimalTypes.filter(defaultType =>
        !customNames.includes(defaultType.name.toLowerCase())
      )
    },

    canSave () {
      return this.form.typeName && (this.selectedType || this.createTypeEnabled)
    }
  },

  methods: {
    ...mapActions('animaltypes', [
      'loadAnimalTypes',
      'loadDefaultAnimalTypes',
      'createAnimalType'
    ]),

    async loadData () {
      const params = {
        farm: this.$route.params.farm_id,
        deleted: 'false',
        limit: 2000
      }
      await this.loadAnimalTypes(params)
      await this.loadDefaultAnimalTypes()
    },

    onSelectType ({ type, isDefault }) {
      this.selectedType = type
      this.isDefaultType = isDefault
      this.createTypeEnabled = true
      this.errors.type = null
    },

    onCreateEnabled (enabled) {
      this.createTypeEnabled = enabled
      if (!enabled) {
        this.selectedType = null
      }
      this.errors.type = null
    },

    checkValidData () {
      this.errors.name = null
      this.errors.type = null

      if (!this.form.name) {
        this.errors.name = ['3']
      } else if (this.form.name.length > 100) {
        this.errors.name = ['6', 100]
      }

      if (!this.form.typeName) {
        this.errors.type = ['3']
      }
    },

    async onUpdate () {
      this.loading = true

      this.checkValidData()

      if (this.errors.name === null && this.errors.type === null) {
        let typeId = null

        if (this.form.animal_type && this.form.animal_type.id) {
          typeId = this.form.animal_type.id
        }

        const typeChanged = this.form.typeName !== this.originalTypeName

        if (typeChanged) {
          const existingCustomType = this.animalTypes.find(t =>
            t.name.toLowerCase() === this.form.typeName.toLowerCase()
          )

          if (!this.selectedType || this.isDefaultType || !existingCustomType) {
            this.creatingNewType = true
            const typePayload = {
              farm: this.$route.params.farm_id,
              name: this.form.typeName
            }
            const typeError = await this.createAnimalType(typePayload)

            if (typeError) {
              this.$toasted.error(this.$t('errors.incorrect_data'))
              this.loading = false
              this.creatingNewType = false
              return
            }

            await this.loadData()

            const newType = this.animalTypes.find(t =>
              t.name.toLowerCase() === this.form.typeName.toLowerCase()
            )

            if (newType) {
              typeId = newType.id
            } else {
              this.$toasted.error(this.$t('errors.type_not_created'))
              this.loading = false
              this.creatingNewType = false
              return
            }

            this.creatingNewType = false
          } else if (this.selectedType) {
            typeId = this.selectedType.id
          }
        }

        const { id, ...payload } = this.form
        payload.animal_type = {
          id: typeId
        }

        const { errors } = await api.groups.updateById(id, payload)
        this.errors = errors

        if (!errors) {
          this.$emit('update')
          this.$emit('close')
        }
      }

      this.loading = false
    },

    async onDelete () {
      await api.groups.deleteById(this.form.id)
      this.$emit('update')
      this.$emit('close')
    }
  }
}
</script>